import { takeLatest } from "redux-saga/effects";
import { defaultReturn, generateDefaultActions, makePayload, requestSaga } from "shared-utility/store/utils/helpers";
import { ToastError, ToastSuccess } from "components/Global/Toasts/Toasty";
import { handleCallback } from "store/utils/helpers";

const initialState = {
  loanProceedsLoanTypeSettings: [],
  loanProceedsAssumptions: {},
  runList: [],
  loanProceedsConstraints: [],
  loanProceedsSpreads: [],
  clonedValues: []
};

// #region Type Settings
export const GET_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS = generateDefaultActions("GET_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS");
export const getLoanProceedsLoanTypeSettings = payload => makePayload(GET_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS.value, payload);
function* getLoanProceedsLoanTypeSettingsSaga() {
  const url = "/loanProceeds/loanTypeSettings";
  yield requestSaga(GET_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS, url, "GET");
}

export const GET_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_ASSUMPTIONS_BY_LOAN_TYPE_ID = generateDefaultActions("GET_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_ASSUMPTIONS_BY_LOAN_TYPE_ID");
export const getLoanProceedsLoanTypeSettingsAssumptions = payload => makePayload(GET_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_ASSUMPTIONS_BY_LOAN_TYPE_ID.value, payload);
function* getLoanProceedsLoanTypeSettingsAssumptionsSaga({ loanTypeId }) {
  const baseUrl = "/loanProceeds/loanTypeSettings/assumptions";
  const url = loanTypeId ? `${baseUrl}/${loanTypeId}` : baseUrl;
  yield requestSaga(GET_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_ASSUMPTIONS_BY_LOAN_TYPE_ID, url, "GET");
}

export const SAVE_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_ASSUMPTIONS = generateDefaultActions("SAVE_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_ASSUMPTIONS");
export const saveLoanProceedsLoanTypeSettingsAssumptions = payload => makePayload(SAVE_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_ASSUMPTIONS.value, payload);
function* saveLoanProceedsLoanTypeSettingsAssumptionsSaga(payload) {
  const url = "/loanProceeds/loanTypeSettings/assumptions";
  yield requestSaga(SAVE_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_ASSUMPTIONS, url, "POST", payload);
}
// #endregion

// #region Spreads and Constraints
export const SAVE_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_SPREADS_AND_CONSTRAINTS = generateDefaultActions("SAVE_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_SPREADS_AND_CONSTRAINTS");
export const saveLoanProceedsLoanTypeSettingsSpreadsAndConstraints = payload => makePayload(SAVE_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_SPREADS_AND_CONSTRAINTS.value, payload);
function* saveLoanProceedsLoanTypeSettingsSpreadsAndConstraintsSaga(payload) {
  const callback = handleCallback(payload);
  const url = "/loanProceeds/loanTypeSettings/spreadMatrices";
  yield requestSaga(SAVE_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_SPREADS_AND_CONSTRAINTS, url, "POST", payload);
  yield callback();
}

export const GET_LOAN_PROCEEDS_UNDERWRITING_CONSTRAINTS = generateDefaultActions("GET_LOAN_PROCEEDS_UNDERWRITING_CONSTRAINTS");
export const getLoanProceedsUnderwritingConstraints = payload => makePayload(GET_LOAN_PROCEEDS_UNDERWRITING_CONSTRAINTS.value, payload);
function* getLoanProceedsUnderwritingConstraintsSaga({ sponsorCompanyId }) {
  const url = `/loanProceeds/underwritingConstraints/${sponsorCompanyId ? `?sponsorCompanyId=${sponsorCompanyId}` : ""}`;
  yield requestSaga(GET_LOAN_PROCEEDS_UNDERWRITING_CONSTRAINTS, url, "GET");
}

export const GET_LOAN_PROCEEDS_SPREADS = generateDefaultActions("GET_LOAN_PROCEEDS_SPREADS");
export const getLoanProceedsSpreads = payload => makePayload(GET_LOAN_PROCEEDS_SPREADS.value, payload);
function* getLoanProceedsSpreadsSaga({ sponsorCompanyId }) {
  const url = `/loanProceeds/spreads/${sponsorCompanyId ? `?sponsorCompanyId=${sponsorCompanyId}` : ""}`;
  yield requestSaga(GET_LOAN_PROCEEDS_SPREADS, url, "GET");
}
// #endregion

// #region Run Table
export const GET_LOAN_PROCEEDS_RUN_TABLE = generateDefaultActions("GET_LOAN_PROCEEDS_RUN_TABLE");
export const getLoanProceedsRunTable = payload => makePayload(GET_LOAN_PROCEEDS_RUN_TABLE.value, payload);
function* getLoanProceedsRunSaga() {
  const url = "/loanProceeds/runList";
  yield requestSaga(GET_LOAN_PROCEEDS_RUN_TABLE, url, "GET");
}

export const SAVE_LOAN_PROCEEDS_RUN_TABLE = generateDefaultActions("SAVE_LOAN_PROCEEDS_RUN_TABLE");
export const saveLoanProceedsRunTable = payload => makePayload(SAVE_LOAN_PROCEEDS_RUN_TABLE.value, payload);
function* saveLoanProceedsRunTableSaga({ checkedItems }) {
  const body = checkedItems.map(item => {
    const option = item.split("#");

    return {
      propertyTypeId: option[0],
      loanTypeId: option[1]
    };
  });
  const url = "/loanProceeds/runList";
  yield requestSaga(SAVE_LOAN_PROCEEDS_RUN_TABLE, url, "POST", body);
}
// #endregion

// #region Calculations
export const LOAN_PROCEEDS_RECALCULATE = generateDefaultActions("LOAN_PROCEEDS_RECALCULATE");
export const loanProceedsRecalculate = payload => makePayload(LOAN_PROCEEDS_RECALCULATE.value, payload);

function* loanProceedsRecalculateSaga({ companyId }) {
  const url = `/loanProceeds/recalculate/${companyId ? companyId : ""}`;
  yield requestSaga(LOAN_PROCEEDS_RECALCULATE, url, "POST");
}
// #endregion

// #region creditAdjustments
export const LOAN_PROCEEDS_UPDATE_CREDIT_ADJUSTMENTS = generateDefaultActions("LOAN_PROCEEDS_UPDATE_CREDIT_ADJUSTMENTS");
export const updateCreditAdjustments = payload => makePayload(LOAN_PROCEEDS_UPDATE_CREDIT_ADJUSTMENTS.value, payload);
function* updateCreditAdjustmentsSaga(payload) {
  const { sponsorCompanyId = null } = payload;
  const url = `/loanProceeds/creditAdjustments/${sponsorCompanyId ? `?sponsorCompanyId=${sponsorCompanyId}` : ""}`;
  yield requestSaga(LOAN_PROCEEDS_UPDATE_CREDIT_ADJUSTMENTS, url, "POST", payload);
}
// #endregion

// #region Spreads and Constraints
export const LOAN_PROCEEDS_CLONE_EXISTING_SPREAD_AND_RUN_VALUES = generateDefaultActions("LOAN_PROCEEDS_CLONE_EXISTING_SPREAD_AND_RUN_VALUES");
export const cloneExistingSpreadAndRunValues = payload => makePayload(LOAN_PROCEEDS_CLONE_EXISTING_SPREAD_AND_RUN_VALUES.value, payload);
function* cloneExistingSpreadAndRunValuesSaga(payload) {
  const { existingCompanyId } = payload;
  const url = `/loanProceeds/cloneExisting/${existingCompanyId}`;
  yield requestSaga(LOAN_PROCEEDS_CLONE_EXISTING_SPREAD_AND_RUN_VALUES, url, "POST", payload);
}
// #endregion

export const loanProceedsReducer = (state = initialState, action) => {
  const { type, payload } = action;
  switch (type) {
    case GET_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS.success:
      return defaultReturn(state, payload.data, "loanProceedsLoanTypeSettings");

    case GET_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_ASSUMPTIONS_BY_LOAN_TYPE_ID.success:
      return defaultReturn(state, payload.data?.[0], "loanProceedsAssumptions");

    case SAVE_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_ASSUMPTIONS.success:
      ToastSuccess("Loan Proceeds Assumptions Successfully Saved");

      return defaultReturn(state, payload.data);

    case SAVE_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_ASSUMPTIONS.failure:
      ToastError("Loan Proceeds Assumptions Failed To Save");

      return defaultReturn(state, payload.data);

    case SAVE_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_SPREADS_AND_CONSTRAINTS.success:
      ToastSuccess("Loan Proceeds Spreads Successfully Saved");

      return defaultReturn(state, payload.data);

    case SAVE_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_SPREADS_AND_CONSTRAINTS.failure:
      ToastError("Loan Proceeds Spreads Failed To Save");

      return defaultReturn(state, payload.data);

    case GET_LOAN_PROCEEDS_UNDERWRITING_CONSTRAINTS.success:
      return defaultReturn(state, payload.data, "loanProceedsConstraints");

    case GET_LOAN_PROCEEDS_SPREADS.success:
      return defaultReturn(state, payload.data, "loanProceedsSpreads");

    case GET_LOAN_PROCEEDS_RUN_TABLE.success:
      return defaultReturn(state, payload.data, "runList");

    case SAVE_LOAN_PROCEEDS_RUN_TABLE.success:
      ToastSuccess("Run Table Successfully Saved");

      return defaultReturn(state, payload.data, "runList");

    case SAVE_LOAN_PROCEEDS_RUN_TABLE.failure:
      ToastError("Run Table Failed To Save");

      return defaultReturn(state, payload.data);

    case LOAN_PROCEEDS_RECALCULATE.success:
      ToastSuccess("Recalculating loan proceeds process has started");

      return defaultReturn(state, payload.data);

    case LOAN_PROCEEDS_RECALCULATE.failure:
      ToastError("Recalculating loan proceeds process failed to start");

      return defaultReturn(state, payload.data);

    case LOAN_PROCEEDS_UPDATE_CREDIT_ADJUSTMENTS.success:
      ToastSuccess("Credit Adjustments Successfully Saved");

      return defaultReturn(state, payload.data);

    case LOAN_PROCEEDS_UPDATE_CREDIT_ADJUSTMENTS.failure:
      ToastError("Updating Credit Adjustment Failure");

      return state;

    case LOAN_PROCEEDS_CLONE_EXISTING_SPREAD_AND_RUN_VALUES.success:
      ToastSuccess("Values successfully cloned, please refresh.");

      return defaultReturn(state, payload.data, "clonedValues");

    case LOAN_PROCEEDS_CLONE_EXISTING_SPREAD_AND_RUN_VALUES.failure:
      ToastError("Cloning values failed, please try again.");

      return state;
    default:
      return state;
  }
};

export function* loanProceedsSaga() {
  yield takeLatest(GET_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS.value, getLoanProceedsLoanTypeSettingsSaga);
  yield takeLatest(GET_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_ASSUMPTIONS_BY_LOAN_TYPE_ID.value, getLoanProceedsLoanTypeSettingsAssumptionsSaga);
  yield takeLatest(SAVE_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_ASSUMPTIONS.value, saveLoanProceedsLoanTypeSettingsAssumptionsSaga);
  yield takeLatest(SAVE_LOAN_PROCEEDS_LOAN_TYPE_SETTINGS_SPREADS_AND_CONSTRAINTS.value, saveLoanProceedsLoanTypeSettingsSpreadsAndConstraintsSaga);
  yield takeLatest(GET_LOAN_PROCEEDS_UNDERWRITING_CONSTRAINTS.value, getLoanProceedsUnderwritingConstraintsSaga);
  yield takeLatest(GET_LOAN_PROCEEDS_SPREADS.value, getLoanProceedsSpreadsSaga);
  yield takeLatest(GET_LOAN_PROCEEDS_RUN_TABLE.value, getLoanProceedsRunSaga);
  yield takeLatest(SAVE_LOAN_PROCEEDS_RUN_TABLE.value, saveLoanProceedsRunTableSaga);
  yield takeLatest(LOAN_PROCEEDS_RECALCULATE.value, loanProceedsRecalculateSaga);
  yield takeLatest(LOAN_PROCEEDS_UPDATE_CREDIT_ADJUSTMENTS.value, updateCreditAdjustmentsSaga);
  yield takeLatest(LOAN_PROCEEDS_CLONE_EXISTING_SPREAD_AND_RUN_VALUES.value, cloneExistingSpreadAndRunValuesSaga);
}
